import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * A pill button with an animated left→right progress fill for long async actions
 * (send, top-up) that have no granular progress events. While `active`, the fill
 * eases toward ~95% (asymptotic "almost there" feel); on `done` it snaps to 100%;
 * on reset (neither active nor done) it clears. The fill is a lighter overlay on
 * top of the button colour, clipped by the pill's rounded corners.
 */
export function ProgressButton({
  active,
  done = false,
  disabled = false,
  onClick,
  className = '',
  children,
  expectedMs = 7000,
}: {
  active: boolean;
  done?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  children: ReactNode;
  /** Rough expected duration — tunes how fast the fill approaches its cap. */
  expectedMs?: number;
}) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (done) {
      setProgress(100);
      return;
    }
    if (!active) {
      setProgress(0);
      return;
    }
    // Asymptotic approach to 95% — decelerates as it goes, so it never stalls at
    // a hard stop and always has room to complete to 100% on `done`.
    const start = performance.now();
    const tau = expectedMs / 2.5;
    const id = window.setInterval(() => {
      const elapsed = performance.now() - start;
      setProgress(95 * (1 - Math.exp(-elapsed / tau)));
    }, 90);
    return () => window.clearInterval(id);
  }, [active, done, expectedMs]);

  const showFill = active || done;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`relative overflow-hidden ${className}`}
    >
      {/* Progress fill — a soft gradient that sweeps left→right beneath the label,
          led by a blurred bright edge so it reads as a liquid "filling" motion. */}
      {showFill && (
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 z-0 bg-gradient-to-r from-white/5 via-white/15 to-white/25 transition-[width] duration-300 ease-out"
          style={{ width: `${progress}%` }}
        >
          <span className="absolute inset-y-0 -right-1 w-6 bg-white/50 blur-[7px]" />
        </span>
      )}
      <span className="relative z-10 flex items-center justify-center gap-2">{children}</span>
    </button>
  );
}
